import { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-hot-toast";
import userApi from "../../api/user/user.api";
import { setAuthModalOpen } from "../../redux/features/authModalSlice";
import { setUser } from "../../redux/features/userSlice";
import { useNavigate } from "react-router-dom";
import ProcessingBar2 from "../ProcessingBar/ProcessingBar2";
import { Eye, EyeOff } from "lucide-react";

const accounts = {
  booking: {
    username: "diamondnailsblenheim@gmail.com",
    password: "di@mond",
  },
  ordering: {
    bbvnstreetfood: {
      username: "bbvnstreetfood@gmail.com",
      password: "123456",
      label: "BBVN Street Food",
    },
    littlevietnam: {
      username: "littlevietnam.nz@gmail.com",
      password: "123456",
      label: "Little Vietnam",
    },
    royal: {
      username: "royal.vietnamese8@gmail.com",
      password: "123456",
      label: "Royal Vietnamese",
    },
  },
  loyalty: {
    username: "hello@rollicious.co.nz",
    password: "r@llicious",
  },
};

const SigninForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);
  const [step, setStep] = useState("login"); // "login" | "otp"
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [expressLogin, setExpressLogin] = useState(false);
  const [masterOtpValues, setMasterOtpValues] = useState([
    "",
    "",
    "",
    "",
    "",
    "",
  ]);
  const masterOtpRefs = useRef([]);

  // --- Step 1: Username & Password ---
  // const signinForm = useFormik({
  //   initialValues: { username: "", password: "" },
  //   validationSchema: Yup.object({
  //     username: Yup.string().required("Username is required"),
  //     password: Yup.string().required("Password is required"),
  //   }),
  //   onSubmit: async (values) => {
  //     setErrorMessage("");
  //     setIsLoading(true);

  //     const { response, err } = await userApi.signin(values);

  //     setIsLoading(false);

  //     if (response?.data?.status === "OTP_SENT") {
  //       toast.success("OTP sent to your email/phone");
  //       setEmail(values.username);
  //       setStep("otp");
  //     }

  //     if (err) {
  //       const msg = err.response?.data?.message || "Login failed";
  //       setErrorMessage(msg);
  //     }
  //   },
  // });

  const signinForm = useFormik({
    initialValues: { username: "", password: "" },
    validationSchema: Yup.object({
      username: Yup.string().required("Username is required"),
      password: Yup.string().required("Password is required"),
      masterOtp: Yup.string().when("expressLogin", {
        is: true, // only validate if expressLogin is true
        then: (schema) =>
          schema
            .required("Master OTP is required")
            .length(6, "Must be 6 digits"),
        otherwise: (schema) => schema.notRequired(),
      }),
    }),
    onSubmit: async (values) => {
      setErrorMessage("");
      setIsLoading(true);

      try {
        // 🚀 Build payload
        let payload = { ...values };

        if (expressLogin) {
          const otp = masterOtpValues.join("");

          // ⛔ Block if empty
          if (!otp) {
            setIsLoading(false);
            setErrorMessage("Master OTP is required for Express Login");
            return;
          }

          // ⛔ Block if not 6 digits
          if (otp.length !== 6) {
            setIsLoading(false);
            setErrorMessage("Please enter the full 6-digit Master OTP");
            return;
          }

          payload.masterOtp = otp; // ✅ use digits from OTP boxes
        }

        console.log({ payload });

        const { response, err } = await userApi.signin(payload);

        setIsLoading(false);

        if (err) {
          const msg = err.response?.data?.message || "Login failed";
          setErrorMessage(msg);
          return;
        }

        // ✅ Express Login successful
        if (expressLogin && response?.data?.token) {
          dispatch(setUser(response.data));
          dispatch(setAuthModalOpen(false));
          toast.success("Login successful with Master OTP");

          const operatorType = (
            response.data?.operatorType || ""
          ).toLowerCase();
          if (operatorType === "ordering") navigate("/orderscalendar");
          else if (operatorType === "booking") navigate("/appointments");
          else if (operatorType === "loyalty") navigate("/loyaltywallet");
          else navigate("/");
          window.location.reload();
          return;
        }

        // ✅ Normal flow
        if (response?.data?.status === "OTP_SENT") {
          toast.success("OTP sent to your email/phone");
          setEmail(values.username);
          setStep("otp");
        }
      } catch (err) {
        setIsLoading(false);
        const msg = err.response?.data?.message || "Login failed";
        setErrorMessage(msg);
        toast.error(msg); // ✅ works for any backend error
      }
    },
  });

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const otp = otpValues.join("");
    if (otp.length !== 6) {
      setErrorMessage("Please enter the full 6-digit OTP");
      setIsLoading(false);
      return;
    }

    try {
      const res = await userApi.verifyOtp({ otp, email });

      // ✅ success
      dispatch(setUser(res.operator)); // use operator
      dispatch(setAuthModalOpen(false));
      toast.success(res.message || "Login successful");

      setTimeout(() => {
        const operatorType = (res.operator?.operatorType || "").toLowerCase();
        if (operatorType === "ordering") navigate("/orderscalendar");
        else if (operatorType === "booking") navigate("/appointments");
        else if (operatorType === "loyalty") navigate("/loyaltywallet");
        else navigate("/");
        window.location.reload();
      }, 300);
    } catch (err) {
      setErrorMessage(err.response?.data?.message || "OTP verification failed");
    }

    setIsLoading(false);
  };

  const handleSignUp = () => {
    setIsProcessing(true);
    setTimeout(() => {
      navigate("/register");
      handleClose();
    }, 2000);
  };

  const handleClose = () => dispatch(setAuthModalOpen(false));

  const [otpValues, setOtpValues] = useState(["", "", "", "", "", ""]);
  const inputsRef = useRef([]);

  const handleChange = (e, index) => {
    const value = e.target.value;
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otpValues];
    newOtp[index] = value.slice(-1);
    setOtpValues(newOtp);

    if (value && index < 5) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otpValues[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text").trim();
    if (!/^\d+$/.test(pasteData)) return;
    const pasteArray = pasteData.split("").slice(0, 6);
    const newOtp = [...otpValues];
    pasteArray.forEach((char, index) => {
      newOtp[index] = char;
    });
    setOtpValues(newOtp);
    const nextIndex = pasteArray.length >= 6 ? 5 : pasteArray.length;
    if (inputsRef.current[nextIndex]) {
      inputsRef.current[nextIndex].focus();
    }
  };

  // auto-fill based on loginType selection
  const [loginType, setLoginType] = useState("booking");
  const [selectedOrdering, setSelectedOrdering] = useState("bbvnstreetfood");
  useEffect(() => {
    if (loginType === "booking") {
      signinForm.setFieldValue("username", accounts.booking.username);
      signinForm.setFieldValue("password", accounts.booking.password);
    } else if (loginType === "ordering") {
      const selected = accounts.ordering[selectedOrdering];
      signinForm.setFieldValue("username", selected.username);
      signinForm.setFieldValue("password", selected.password);
    } else if (loginType === "loyalty") {
      signinForm.setFieldValue("username", accounts.loyalty.username);
      signinForm.setFieldValue("password", accounts.loyalty.password);
    }
  }, [loginType, selectedOrdering]);

  return (
    <form
      onSubmit={step === "login" ? signinForm.handleSubmit : handleOtpSubmit}
      className="space-y-4"
    >
      {step === "login" && (
        <>
          {/* Username Input */}
          <input
            type="text"
            name="username"
            placeholder="Email"
            className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-redlight"
            value={signinForm.values.username}
            onChange={signinForm.handleChange}
          />

          {/* Password Input */}
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              placeholder="Password"
              className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-redlight"
              value={signinForm.values.password}
              onChange={signinForm.handleChange}
            />
            <div
              className="absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer text-gray-500"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </div>
          </div>

          {/* Login type */}
          <div className="flex items-center justify-center gap-6 mb-4 text-text">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="loginType"
                value="booking"
                checked={loginType === "booking"}
                onChange={(e) => setLoginType(e.target.value)}
              />
              <span className="text-sm">Booking</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="loginType"
                value="ordering"
                checked={loginType === "ordering"}
                onChange={(e) => setLoginType(e.target.value)}
              />
              <span className="text-sm">Ordering</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="loginType"
                value="loyalty"
                checked={loginType === "loyalty"}
                onChange={(e) => setLoginType(e.target.value)}
              />
              <span className="text-sm">Loyalty</span>
            </label>
          </div>

          {loginType === "ordering" && (
            <select
              className="w-full px-3 py-2 border rounded-md"
              value={selectedOrdering}
              onChange={(e) => setSelectedOrdering(e.target.value)}
            >
              {Object.entries(accounts.ordering).map(([key, value]) => (
                <option key={key} value={key}>
                  {value.label}
                </option>
              ))}
            </select>
          )}

          <hr className="w-16 mx-auto border-t border-redlight " />
          {/* Express Login Checkbox */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={expressLogin}
              onChange={(e) => setExpressLogin(e.target.checked)}
            />
            <span className="text-sm font-medium text-text">Express Login</span>
          </label>

          {expressLogin && (
            <div className="flex justify-center space-x-2 mb-4">
              {masterOtpValues.map((value, index) => (
                <input
                  key={index}
                  ref={(el) => (masterOtpRefs.current[index] = el)}
                  type="tel"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength="1"
                  value={value}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (!/^\d*$/.test(val)) return;
                    const newOtp = [...masterOtpValues];
                    newOtp[index] = val.slice(-1);
                    setMasterOtpValues(newOtp);
                    if (val && index < 5)
                      masterOtpRefs.current[index + 1]?.focus();
                  }}
                  onKeyDown={(e) => {
                    if (
                      e.key === "Backspace" &&
                      !masterOtpValues[index] &&
                      index > 0
                    ) {
                      masterOtpRefs.current[index - 1]?.focus();
                    }
                  }}
                  onPaste={(e) => {
                    e.preventDefault();
                    const paste = e.clipboardData.getData("text").trim();
                    if (!/^\d+$/.test(paste)) return;
                    const digits = paste.split("").slice(0, 6);
                    const newOtp = [...masterOtpValues];
                    digits.forEach((d, i) => {
                      newOtp[i] = d;
                    });
                    setMasterOtpValues(newOtp);
                    const nextIndex = digits.length >= 6 ? 5 : digits.length;
                    if (masterOtpRefs.current[nextIndex]) {
                      masterOtpRefs.current[nextIndex].focus();
                    }
                  }}
                  className="w-10 h-12 sm:w-12 border rounded text-center text-xl focus:outline-none focus:ring-2 focus:ring-redlight"
                />
              ))}
            </div>
          )}

          {expressLogin && (
            <br className="w-16 mx-auto border-t border-border " />
          )}

          {signinForm.errors.masterOtp && (
            <p className="text-red-500 text-sm">
              {signinForm.errors.masterOtp}
            </p>
          )}
        </>
      )}

      {step === "otp" && (
        <>
          <p className="text-sm text-center text-gray-600">
            Enter the 6-digit OTP sent to <b>{email}</b>
          </p>

          <div className="flex justify-center space-x-2 mb-6 mt-4">
            {otpValues.map((value, index) => (
              <input
                key={index}
                ref={(el) => (inputsRef.current[index] = el)}
                type="tel" // ✅ triggers numeric keypad on iOS/Android
                inputMode="numeric" // ✅ tells browsers this should be numbers only
                pattern="[0-9]*" // ✅ extra safety for numeric digits                maxLength="1"
                value={value}
                onChange={(e) => handleChange(e, index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                onPaste={handlePaste}
                className="w-12 h-12 border rounded text-center text-xl focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            ))}
          </div>
        </>
      )}

      {/* Submit Button */}
      <button
        type="submit"
        disabled={isLoading}
        className="w-full text-black hover:text-white py-2 rounded-md transition duration-300"
        style={{ background: "linear-gradient(-45deg, #f87171, #991b1b)" }}
      >
        {isLoading ? (
          <div className="flex justify-center items-center gap-4 w-100 h-6">
            <ProcessingBar2 />
          </div>
        ) : expressLogin ? (
          "Login with Master OTP"
        ) : step === "login" ? (
          "Next"
        ) : (
          "Verify OTP"
        )}
      </button>

      {/* Sign Up Link */}
      {step === "login" && (
        <button
          type="button"
          onClick={handleSignUp}
          className="w-full text-center text-redlight hover:text-white mt-4 py-2 rounded-md border border-redlight"
        >
          {isProcessing ? (
            <div className="flex justify-center items-center gap-4 w-100 h-6">
              <ProcessingBar2 />
            </div>
          ) : (
            "Sign Up"
          )}
        </button>
      )}
      {errorMessage && (
        <p className="text-red-500 text-sm text-center">{errorMessage}</p>
      )}
    </form>
  );
};

export default SigninForm;
